import { Injectable } from '@angular/core';
import { assert, Deferred, isDefined } from '@sama/js-core';
import { sortedIndex } from 'lodash';
import { RetryableRequestResult } from '../../../cross-team-shared/misc-services/resources/url-request.service';
import { TestHookService } from '../../../shared/misc-services/test-hook.service';

export const loadAssetHook = Symbol('load asset test hook');

interface QueueItem {
  sendRequest: () => Promise<RetryableRequestResult>;
  deferred: Deferred<RetryableRequestResult>;
}

// Queue containing all pending requests for a fusion. Requests are prioritized
// by how close they are to that fusion's pending frame number.
interface RequestQueue {
  requests: Map<number, QueueItem[]>;
  pendingFrameNumber: number;
}

// Chrome limits us to 6 concurrent in flight requests. We want to save one
// slot for non-asset requests, so at most we will use 5.
const targetInFlight = 5;
const ratioAfter = 4;
const ratioBefore = 1;

@Injectable()
export class SmartLoaderService {
  // One queue per fusion, keyed by its primary output's id
  private queues = new Map<number, RequestQueue>();
  private numInFlightRequests = 0;

  constructor(private testHookService: TestHookService) {}

  queueRequest(
    primaryOutputId: number,
    frameNumber: number,
    sendRequest: QueueItem['sendRequest'],
  ) {
    const deferred = new Deferred<RetryableRequestResult>();
    const queue = this.getQueue(primaryOutputId);

    this.addToQueue(queue, frameNumber, { sendRequest, deferred });

    void this.startNextAsset();
    return deferred.promise;
  }

  setPendingFrameNumber(primaryOutputId: number, frameNumber: number) {
    this.getQueue(primaryOutputId).pendingFrameNumber = frameNumber;
  }

  cancel(fusionOutputId: number) {
    this.queues.delete(fusionOutputId);
  }

  private getQueue(primaryOutputId: number) {
    if (!this.queues.has(primaryOutputId)) {
      const newQueue = {
        requests: new Map<number, QueueItem[]>(),
        pendingFrameNumber: 0,
      };
      this.queues.set(primaryOutputId, newQueue);
    }
    return this.queues.get(primaryOutputId)!;
  }

  private dequeue() {
    let closestFrameNumber: number | undefined;
    let closestQueue: RequestQueue | undefined;
    let smallestDistance = Infinity;

    for (const queue of this.queues.values()) {
      const next = this.getNextFrameNumber(queue);
      if (
        isDefined(next) &&
        Math.abs(next - queue.pendingFrameNumber) < smallestDistance
      ) {
        closestFrameNumber = next;
        closestQueue = queue;
        smallestDistance = Math.abs(next - queue.pendingFrameNumber);
      }
    }

    // Stop condition: If we hit this it means all queues are empty
    if (closestQueue === undefined) {
      return undefined;
    }

    return this.dequeueFrom(closestQueue, closestFrameNumber!);
  }

  private addToQueue(
    queue: RequestQueue,
    frameNumber: number,
    queueItem: QueueItem,
  ) {
    const existing = queue.requests.get(frameNumber);
    if (existing) {
      existing.push(queueItem);
    } else {
      queue.requests.set(frameNumber, [queueItem]);
    }
  }

  private dequeueFrom(queue: RequestQueue, frameNumber: number) {
    const existing = queue.requests.get(frameNumber);
    assert(existing, 'You must pass in a legal frame number');

    const result = existing.shift();
    if (existing.length === 0) {
      queue.requests.delete(frameNumber);
    }
    return result;
  }

  // Returns undefined if the queue is empty
  private getNextFrameNumber(queue: RequestQueue) {
    const closestBefore = this.getClosestBefore(queue);
    const closestAfter = this.getClosestAfter(queue);

    if (!isDefined(closestBefore)) {
      return closestAfter;
    }
    if (!isDefined(closestAfter)) {
      return closestBefore;
    }

    return this.getClosestRespectingRatio(
      closestBefore,
      closestAfter,
      queue.pendingFrameNumber,
    );
  }

  private getClosestBefore(queue: RequestQueue) {
    const keys = Array.from(queue.requests.keys()).sort((a, b) => a - b);
    const currentFrameIndex = this.getSortedIndex(
      keys,
      queue.pendingFrameNumber,
    );
    if (keys[currentFrameIndex] >= queue.pendingFrameNumber) {
      return keys[currentFrameIndex - 1];
    } else {
      return keys[currentFrameIndex];
    }
  }

  private getClosestAfter(queue: RequestQueue) {
    const keys = Array.from(queue.requests.keys()).sort((a, b) => a - b);
    const currentFrameIndex = this.getSortedIndex(
      keys,
      queue.pendingFrameNumber,
    );
    if (keys[currentFrameIndex] < queue.pendingFrameNumber) {
      return undefined;
    }
    return keys[currentFrameIndex];
  }

  private getClosestRespectingRatio(
    closestBefore: number,
    closestAfter: number,
    pendingFrameNumber: number,
  ) {
    const distanceBefore = pendingFrameNumber - closestBefore;
    const distanceAfter = closestAfter - pendingFrameNumber;
    if (distanceAfter / ratioAfter < distanceBefore / ratioBefore) {
      return closestAfter;
    } else {
      return closestBefore;
    }
  }

  private async startNextAsset() {
    if (this.numInFlightRequests >= targetInFlight || this.queues.size === 0) {
      return;
    }

    const nextRequest = this.dequeue();
    if (!nextRequest) {
      return;
    }

    ++this.numInFlightRequests;
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- fix me!
    const { sendRequest, deferred } = nextRequest!;
    try {
      this.testHookService.run(loadAssetHook);
      deferred.resolve(await sendRequest());
    } catch (error) {
      deferred.reject(error);
    } finally {
      --this.numInFlightRequests;
    }

    void this.startNextAsset();
  }

  private getSortedIndex(array: any[], value: any): number {
    const index = sortedIndex(array, value);
    return index === array.length ? index - 1 : index;
  }
}
